import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getMyProfile, updateMyPreferences, refreshMyDataFromGestao } from "@/lib/user.functions";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTheme } from "@/components/theme-provider";
import { Sun, Moon, Monitor, Loader2 } from "lucide-react";
import { EmailSyncBanner } from "@/components/email-sync-banner";
import type { EmailSyncStatus } from "@/lib/email-sync-policy";
import { cn } from "@/lib/utils";
import { formatCpf } from "@/lib/cpf";
import { GESTAO_URL, toastSensitiveFieldLocked } from "@/lib/sensitive-field-errors";
import { SensitiveFieldsNotice } from "@/components/sensitive-fields-notice";

/** Formata `YYYY-MM-DD` como `DD/MM/AAAA`. */
function formatBirthDate(value?: string | null) {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
}

export const Route = createFileRoute("/_authenticated/perfil/")({
  head: () => ({ meta: [{ title: "Meu perfil · Beit Tefilah Rechovot" }] }),
  component: PerfilPage,
});

// A foto de perfil é definida exclusivamente na Gestão de cadastros e chega
// aqui pela sincronização; esta plataforma apenas exibe a imagem.

type Prefs = {
  pref_default_turno: "todos" | "manha" | "tarde";
  pref_items_per_page: 12 | 24 | 48;
  pref_autoplay: boolean;
  pref_hide_watched: boolean;
  pref_resume_playback: boolean;
  live_notification_pref: "off" | "shabbat" | "all";
};

const DEFAULT_PREFS: Prefs = {
  pref_default_turno: "todos",
  pref_items_per_page: 24,
  pref_autoplay: false,
  pref_hide_watched: false,
  pref_resume_playback: true,
  live_notification_pref: "off",
};

function PerfilPage() {
  const qc = useQueryClient();
  const getFn = useServerFn(getMyProfile);
  const prefsFn = useServerFn(updateMyPreferences);
  const refreshFn = useServerFn(refreshMyDataFromGestao);
  const { theme, setTheme } = useTheme();

  const { data: profile } = useQuery({ queryKey: ["my-profile"], queryFn: () => getFn() });
  const [fullName, setFullName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [prefs, setPrefs] = useState<Prefs>(DEFAULT_PREFS);
  const [currentEmail, setCurrentEmail] = useState("");

  /** Avisa que dados pessoais só mudam na Gestão quando o membro tenta digitar. */
  const warnLockedField = (e: React.SyntheticEvent) => {
    e.preventDefault();
    toastSensitiveFieldLocked();
  };

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? "");
      setAvatarUrl(profile.avatar_url ?? "");
      const p = profile as any;
      setPrefs({
        pref_default_turno: p.pref_default_turno ?? "todos",
        pref_items_per_page: (p.pref_items_per_page ?? 24) as Prefs["pref_items_per_page"],
        pref_autoplay: !!p.pref_autoplay,
        pref_hide_watched: !!p.pref_hide_watched,
        pref_resume_playback: p.pref_resume_playback ?? true,
        live_notification_pref: p.live_notification_pref ?? "off",
      });
    }
  }, [profile]);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.auth.getUser();
      setCurrentEmail(data.user?.email ?? "");
    })();
  }, []);

  const savePrefs = useMutation({
    mutationFn: (patch: Partial<Prefs>) => prefsFn({ data: patch as any }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-profile"] });
      toast.success("Preferências salvas.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao salvar preferências"),
  });

  const setPref = <K extends keyof Prefs>(key: K, value: Prefs[K]) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    savePrefs.mutate({ [key]: value } as Partial<Prefs>);
  };

  // Conta (e-mail/senha) e dados pessoais são gerenciados exclusivamente na
  // Gestão de cadastros: nada disso pode ser alterado por esta plataforma.

  const refresh = useMutation({
    mutationFn: () => refreshFn(),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["my-profile"] });
      qc.invalidateQueries({ queryKey: ["session"] });
      qc.invalidateQueries({ queryKey: ["access-status"] });
      if (res?.found === false) toast.error(res.message ?? "Cadastro não localizado na Gestão.");
      else toast.success("Dados atualizados conforme a Gestão de cadastros.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível atualizar seus dados agora."),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <EmailSyncBanner
        status={(profile as any)?.email_sync_status as EmailSyncStatus | undefined}
      />
      <div>
        <h1 className="font-serif text-3xl font-semibold text-primary">Meu perfil</h1>
        <p className="mt-1 text-muted-foreground">
          Consulte seus dados da Gestão de cadastros e ajuste suas preferências.
        </p>
      </div>

      <section className="space-y-4 rounded-xl border border-border bg-card p-6">
        <h2 className="font-serif text-xl font-semibold text-primary">Foto de perfil</h2>
        <p className="text-sm text-muted-foreground">
          Sua foto é sincronizada com a Gestão de cadastros e não pode ser enviada ou alterada nesta
          plataforma. Para trocar a imagem, atualize o cadastro na Gestão.
        </p>
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-lg font-semibold text-primary">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Foto de perfil" className="h-full w-full object-cover" />
            ) : (
              <span>{(fullName || "?").slice(0, 1).toUpperCase()}</span>
            )}
          </div>
          <Button asChild variant="outline">
            <a href={GESTAO_URL} target="_blank" rel="noopener noreferrer">
              Atualizar foto na Gestão
            </a>
          </Button>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-border bg-card p-6">
        <h2 className="font-serif text-xl font-semibold text-primary">Dados pessoais</h2>
        <p className="text-sm text-muted-foreground">
          Estes dados vêm da Gestão de cadastros e são exibidos aqui apenas para consulta.
        </p>
        <div className="space-y-2">
          <Label htmlFor="name">Nome completo</Label>
          <Input
            id="name"
            value={fullName || "—"}
            readOnly
            onChange={warnLockedField}
            onBeforeInput={warnLockedField}
            onPaste={warnLockedField}
            className="cursor-not-allowed bg-muted/50"
            aria-describedby="dados-bloqueados"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="cpf">CPF</Label>
            <Input
              id="cpf"
              value={formatCpf((profile as any)?.cpf ?? "") || "—"}
              readOnly
              onChange={warnLockedField}
              onBeforeInput={warnLockedField}
              onPaste={warnLockedField}
              className="cursor-not-allowed bg-muted/50"
              aria-describedby="dados-bloqueados"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="birth">Data de nascimento</Label>
            <Input
              id="birth"
              value={formatBirthDate((profile as any)?.birth_date)}
              readOnly
              onChange={warnLockedField}
              onBeforeInput={warnLockedField}
              onPaste={warnLockedField}
              className="cursor-not-allowed bg-muted/50"
              aria-describedby="dados-bloqueados"
            />
          </div>
        </div>
        <SensitiveFieldsNotice id="dados-bloqueados" />

        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <a href={GESTAO_URL} target="_blank" rel="noopener noreferrer">
              Alterar meus dados na Gestão de cadastros
            </a>
          </Button>
          <Button variant="ghost" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
            {refresh.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Atualizando...
              </>
            ) : (
              "Atualizar dados agora"
            )}
          </Button>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-border bg-card p-6">
        <h2 className="font-serif text-xl font-semibold text-primary">Aparência</h2>
        <p className="text-sm text-muted-foreground">Escolha o tema que mais lhe agrada.</p>
        <div className="grid grid-cols-3 gap-3">
          {(
            [
              { v: "light", label: "Claro", icon: Sun },
              { v: "dark", label: "Escuro", icon: Moon },
              { v: "system", label: "Sistema", icon: Monitor },
            ] as const
          ).map(({ v, label, icon: Icon }) => (
            <button
              key={v}
              onClick={() => setTheme(v)}
              className={cn(
                "flex flex-col items-center gap-2 rounded-lg border p-4 transition-colors",
                theme === v
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-border hover:bg-secondary",
              )}
            >
              <Icon className="h-5 w-5" />
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-5 rounded-xl border border-border bg-card p-6">
        <div>
          <h2 className="font-serif text-xl font-semibold text-primary">Preferências de uso</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Estas escolhas personalizam sua navegação e são salvas automaticamente.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Turno preferido no catálogo</Label>
            <Select
              value={prefs.pref_default_turno}
              onValueChange={(v) => setPref("pref_default_turno", v as Prefs["pref_default_turno"])}
            >
              <SelectTrigger aria-label="Turno preferido no catálogo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os turnos</SelectItem>
                <SelectItem value="manha">Manhã (11h)</SelectItem>
                <SelectItem value="tarde">Tarde (15h30)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Ensinos por página</Label>
            <Select
              value={String(prefs.pref_items_per_page)}
              onValueChange={(v) =>
                setPref("pref_items_per_page", Number(v) as Prefs["pref_items_per_page"])
              }
            >
              <SelectTrigger aria-label="Ensinos por página">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12">12 por página</SelectItem>
                <SelectItem value="24">24 por página</SelectItem>
                <SelectItem value="48">48 por página</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label>Avisos de transmissão ao vivo</Label>
            <Select
              value={prefs.live_notification_pref}
              onValueChange={(v) =>
                setPref("live_notification_pref", v as Prefs["live_notification_pref"])
              }
            >
              <SelectTrigger aria-label="Avisos de transmissão ao vivo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Não quero receber avisos</SelectItem>
                <SelectItem value="shabbat">Somente lives de Shabat</SelectItem>
                <SelectItem value="all">Todas as transmissões</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          {(
            [
              {
                key: "pref_autoplay",
                label: "Iniciar o vídeo automaticamente",
                hint: "Ao abrir um ensino, a reprodução começa sozinha.",
              },
              {
                key: "pref_resume_playback",
                label: "Continuar de onde parei",
                hint: "Retoma o vídeo no ponto em que você parou.",
              },
              {
                key: "pref_hide_watched",
                label: "Ocultar ensinos já assistidos",
                hint: "Remove das listagens os ensinos concluídos.",
              },
            ] as const
          ).map((row) => (
            <div key={row.key} className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{row.label}</p>
                <p className="text-xs text-muted-foreground">{row.hint}</p>
              </div>
              <Switch
                checked={prefs[row.key]}
                onCheckedChange={(v) => setPref(row.key, v)}
                aria-label={row.label}
                className="relative mt-0.5 shrink-0 after:absolute after:-inset-3 after:content-['']"
              />
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-6">
        <h2 className="font-serif text-xl font-semibold text-primary">E-mail e senha</h2>
        <p className="text-sm text-muted-foreground">
          Os dados de acesso (e-mail, senha e dados cadastrais) são gerenciados exclusivamente na
          Gestão de cadastros da congregação. Esta plataforma não envia e-mails de conta nem de
          redefinição de senha.
        </p>
        <p className="text-sm text-foreground">
          E-mail atual: <span className="font-medium">{currentEmail || "—"}</span>
        </p>
        <Button asChild variant="outline">
          <a href="https://cadastro.rechovot.org.br" target="_blank" rel="noopener noreferrer">
            Gerenciar minha conta na Gestão de cadastros
          </a>
        </Button>
      </section>
    </div>
  );
}
